import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { MenuItem, Select } from '@material-ui/core'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import React, { useCallback } from 'react'
import {
    TRACK_CHANGE_STATUS_LABELS,
    TRACK_CHANGE_STATUS_VALUES,
} from '../../constants'
import { useTrackChangesFilterStyles } from '../../styles'
import { useTrackChangesFilter } from './hooks/useTrackChangesFilter'

/**
 * TrackChangesFilter component renders a filter dropdown for track changes.
 * It allows users to filter changes based on their resolution type.
 *
 * @returns {JSX.Element} - The rendered filter dropdown component.
 */
export const TrackChangesFilter = React.memo(() => {
    const classes = useTrackChangesFilterStyles()
    const { suggestionCardFilter } = useSuggestionsShallowSelector((state) => ({
        suggestionCardFilter: state.suggestionCardFilter,
    }))
    const { handleFilterResolutionTypeChange } = useTrackChangesFilter()

    const menuProps = React.useMemo(
        () => ({
            getContentAnchorEl: null,
            anchorOrigin: {
                vertical: 'bottom' as 'bottom',
                horizontal: 'left' as 'left',
            },
            transformOrigin: {
                vertical: 'top' as 'top',
                horizontal: 'left' as 'left',
            },
            PaperProps: {
                'aria-label': 'Resolution Type Filter Options',
            },
        }),
        []
    )

    const getTrackChangeOptions = useCallback((): React.ReactElement[] => {
        return (Object.keys(TRACK_CHANGE_STATUS_LABELS) as Array<
            keyof typeof TRACK_CHANGE_STATUS_LABELS
        >).map((status) => (
            <MenuItem
                className={classes.filterOptionItem}
                key={status}
                value={TRACK_CHANGE_STATUS_VALUES[status]}
                title={TRACK_CHANGE_STATUS_LABELS[status]}
                aria-label={`Filter by ${TRACK_CHANGE_STATUS_LABELS[status]}`}
                aria-selected={
                    suggestionCardFilter === TRACK_CHANGE_STATUS_VALUES[status]
                }
            >
                {TRACK_CHANGE_STATUS_LABELS[status]}
            </MenuItem>
        ))
    }, [classes.filterOptionItem])

    const options = React.useMemo(() => getTrackChangeOptions(), [classes])

    return (
        <GridItem
            className={classes.filterContainer}
            data-testid="filterGridItem"
        >
            <Select
                data-testid="filterDropdownTrackChanges"
                value={suggestionCardFilter}
                onChange={handleFilterResolutionTypeChange}
                variant="outlined"
                className={classes.selectInput}
                classes={{
                    outlined: classes.selectSelectOutlined,
                }}
                aria-label="Resolution Type Filter"
                title="Resolution Type Filter"
                MenuProps={menuProps}
            >
                {options}
            </Select>
        </GridItem>
    )
})
